import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getTenantFromUrl, getTenantHeaders, TenantInfo } from '../utils/enhancedTenantUtils';
import api from '../api';

interface TenantData {
  id: number;
  subdomain: string;
  name: string;
  status: string;
  subscription_tier: string;
  max_users: number;
  max_projects: number;
  storage_limit_gb: number;
  branding?: {
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
    companyName?: string;
  };
  settings?: Record<string, any>;
  usage?: {
    users: number;
    projects: number;
    documents: number;
    maxUsers: number;
    maxProjects: number;
  };
}

interface TenantContextType {
  tenantInfo: TenantInfo;
  tenantData: TenantData | null;
  isLoading: boolean;
  error: string | null;
  refreshTenantData: () => Promise<void>;
  isActive: boolean;
  canCreateProject: boolean;
  canCreateUser: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantInfo] = useState<TenantInfo>(() => getTenantFromUrl());
  const [tenantData, setTenantData] = useState<TenantData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTenantData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Skip tenant data fetch if we're on main domain
      if (tenantInfo.isMainDomain) {
        setTenantData(null);
        return;
      }

      const response = await api.get('/api/tenants/current');
      const { tenant } = response.data;
      
      setTenantData(tenant);
    } catch (err: any) {
      console.error('Error fetching tenant data:', err);
      
      // Handle specific error cases
      if (err.response?.status === 404) {
        setError('Tenant not found');
      } else if (err.response?.status === 403) {
        setError('Tenant is inactive or suspended');
      } else if (err.response?.data?.code === 'TENANT_NOT_FOUND') {
        setError('Invalid subdomain');
      } else {
        setError('Failed to load tenant information');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTenantData = async () => {
    await fetchTenantData();
  };

  // Derived state
  const isActive = tenantInfo.isMainDomain ? true : (
    tenantData?.status === 'active' || 
    (tenantData?.status === 'trial' && new Date() < new Date())
  );

  const canCreateProject = tenantInfo.isMainDomain ? true : (
    tenantData?.usage ? 
    tenantData.usage.projects < tenantData.usage.maxProjects : 
    false
  );

  const canCreateUser = tenantInfo.isMainDomain ? true : (
    tenantData?.usage ? 
    tenantData.usage.users < tenantData.usage.maxUsers : 
    false
  );

  useEffect(() => {
    fetchTenantData();
  }, [tenantInfo.subdomain]);

  // Apply tenant branding to document
  useEffect(() => {
    if (tenantData?.branding) {
      const { primaryColor, secondaryColor, companyName } = tenantData.branding;
      
      // Update CSS custom properties for theming
      const root = document.documentElement;
      
      if (primaryColor) {
        root.style.setProperty('--tenant-primary-color', primaryColor);
      }
      
      if (secondaryColor) {
        root.style.setProperty('--tenant-secondary-color', secondaryColor);
      }
      
      // Update page title if company name is provided
      if (companyName) {
        document.title = `JARVIS RCSA - ${companyName}`;
      }
    }
    
    return () => {
      // Reset to defaults when component unmounts
      const root = document.documentElement;
      root.style.removeProperty('--tenant-primary-color');
      root.style.removeProperty('--tenant-secondary-color');
      document.title = 'JARVIS RCSA Platform';
    };
  }, [tenantData?.branding]);

  const value: TenantContextType = {
    tenantInfo,
    tenantData,
    isLoading,
    error,
    refreshTenantData,
    isActive,
    canCreateProject,
    canCreateUser
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;